<script lang="ts" setup>
import { computed, watch } from 'vue'
import { useField } from 'vee-validate'
import type { FormField } from '../types/formTypeStructure'
import {
  useResponsiveSize,
  getLocalizedText,
} from '../utils/formHelpers'
import FormFieldWrapper from '../FormFieldWrapper.vue'
import { AlertCircle } from "lucide-vue-next";

interface Props {
  field: FormField & { fieldType: 'radio'; selectOptions: { label: string | Record<string, string>; value: string }[] }
  name?: string
  defaultValue?: string
  currentLang?: string
  className?: string
}

const props = defineProps<Props>()
const emit = defineEmits<{
  'update:modelValue': [value: string];
  'change': [value: string];
  'input': [value: string];
}>();

const size = useResponsiveSize()
const fieldName = props.name ?? props.field.fieldName.toString()
const label = computed(() => getLocalizedText(props.field.label, props.currentLang ?? 'fr'))
const isRequired = computed(() => props.field.validations?.some(v => v.validationType === 'required'))

const {
  value,
  errorMessage,
  handleChange,
  setTouched,
} = useField<string>(fieldName, undefined, {
  initialValue: props.defaultValue ?? ''
})

// Émettre les événements quand la valeur change
watch(value, (newValue) => {
  emit('update:modelValue', newValue);
  emit('change', newValue);
  emit('input', newValue);
});

const inputStyle = computed(() => {
  const { width, height, borderRadius } = size.calculateSize(window.innerWidth)
  return {
    width: `${width}px`,
    height: `${height}px`,
    borderRadius: `${borderRadius}px`,
  }
})

const baseInputClasses =
  'appearance-none w-4 h-4 border-2 border-gray-300 rounded-full transition-all duration-200 focus:ring-2 focus:ring-[#04406E] focus:ring-offset-2 checked:bg-[#04406E] checked:border-[#04406E]'
</script>

<template>
  <div class="radio-field-container">
    <!-- Label dupliqué au-dessus des radios -->
    <div class="radio-field-label-container">
      <span class="radio-field-label-text">
        {{ label }}
        <AlertCircle
          v-if="isRequired"
          :size="16"
          class="inline text-red-500 ml-2"
        />
      </span>
    </div>

    <FormFieldWrapper :field="props.field" :current-lang="props.currentLang">
      <div class="flex flex-col gap-3">
        <label
          v-for="(option, idx) in props.field.selectOptions"
          :key="option.value"
          :for="`${props.field.formFieldId}-${option.value}-${idx}`"
          class="relative flex items-center gap-3 cursor-pointer p-3 rounded-lg border border-gray-200 hover:border-[#04406E] hover:bg-[#e6f0f5] transition-all duration-200 group"
        >
          <div class="relative flex-shrink-0">
            <input
              type="radio"
              :id="`${props.field.formFieldId}-${option.value}-${idx}`"
              :name="fieldName"
              :value="option.value"
              :checked="value === option.value"
              :class="[baseInputClasses, props.className]"
              @change="(e) => { handleChange((e.target as HTMLInputElement).value); setTouched(true); }"
              @blur="() => setTouched(true)"
            />
            <!-- Custom radio dot -->
            <div 
              class="absolute inset-0 flex items-center justify-center pointer-events-none"
              :class="value === option.value ? 'opacity-100' : 'opacity-0'"
            >
              <div class="w-2 h-2 bg-[#04406E] rounded-full transition-opacity duration-200"></div>
            </div>
          </div>
          <span class="text-gray-700 font-medium group-hover:text-[#04406E] transition-colors duration-200">
            {{ getLocalizedText(option.label, props.currentLang ?? 'fr') }}
          </span>
        </label>
      </div>

      <p v-if="errorMessage" class="text-sm text-red-500 mt-2">
        {{ errorMessage }}
      </p>
    </FormFieldWrapper>
  </div>
</template>

<style scoped>
.radio-field-container {
  position: relative;
  display: block;
  width: 100%;
}

.radio-field-label-container {
  position: relative;
  display: block;
  margin-bottom: 0.75rem;
  z-index: 100;
}

.radio-field-label-text {
  display: block;
  font-size: 1.125rem;
  font-weight: 700;
  color: #333333;
  line-height: 1.2;
  margin: 0;
  padding: 0;
  position: static !important;
  transform: none !important;
  top: auto !important;
  left: auto !important;
  right: auto !important;
  bottom: auto !important;
  height: auto !important;
  overflow: visible !important;
  text-align: left !important;
  text-overflow: unset !important;
  white-space: normal !important;
  pointer-events: auto !important;
  border: none !important;
  transform-origin: unset !important;
  transition: none !important;
}
</style>
